import OrdersAgent from "../agents/OrdersAgent";
import {createEffect, createResource, createSignal} from "solid-js";

// Miroir de RESERVATIONS_PAGE_SIZE (backend : plugins/plateforme-ccpl/common/constants.ts).
// Le backend replafonne de toute façon "take" à 100.
export const RESERVATIONS_PAGE_SIZE = 20;

export function Orders(store) {

    // Agent
    const agent = new OrdersAgent(store);

    // Init
    store.on('@init', (state) => ({
        agents: {
            ...state.agents,
            orders: agent,
        },
        loading: {
            ...state.loading,
            orders: true,
            myReservations: true,
            orderReservations: true
        },
        orders: {},
        myReservations: null,
        myReservationsTotal: 0,
        myReservationsSkip: 0,
        myReservationsError: false,
        orderReservations: null,
        orderReservationsCode: null,
        orderReservationsError: false,
        orderReservationsUnknownCode: false,
        cancelReservationResult: null,
        cancellingReservation: null
    }));

    // Actions
    const [getOne, setGetOne] = createSignal(false);
    const [byOrderCode] = createResource(getOne, async (orderCode) => [orderCode, await agent.getOrder(orderCode)]);
    createEffect(() => byOrderCode() && store.dispatch("setOrder", byOrderCode()));
    createEffect(() => store.dispatch("setOrdersLoading", byOrderCode.loading));
    store.on('setOrdersLoading', (state, orders) => ({loading:{...state.loading, orders}}))

    store.on('loadOrder', (state, orderCode) => {setGetOne(orderCode)});
    store.on('setOrder', (state, [orderCode, order]) => ({ orders: { ...state.orders, [orderCode]: order } }));

    // Réservations de l'utilisateur courant (canal par défaut => toutes institutions).
    // Le signal porte la page demandée : le changer suffit à refetcher.
    const [getReservations, setGetReservations] = createSignal(false);
    const [reservations, { refetch: refetchReservations }] = createResource(
        getReservations,
        (page) => agent.getMyReservations({ skip: page.skip, take: page.take })
    );
    createEffect(() => {
        const result = reservations();
        if (result !== undefined) {
            store.dispatch("setMyReservations", result);
        }
    });
    // On ne pousse le loading que lorsqu'il passe à true ; le false est posé
    // dans setMyReservations (idem pattern store/Users.js).
    createEffect(() => reservations.loading && store.dispatch("setMyReservationsLoading", reservations.loading));
    store.on('setMyReservationsLoading', (state, myReservations) => ({loading:{...state.loading, myReservations}}));

    store.on('loadMyReservations', (state, params) => {
        const skip = (params && typeof params.skip === "number") ? params.skip : state.myReservationsSkip;
        // Objet neuf à chaque fois : createResource compare par référence, donc
        // recharger la même page (après une annulation) doit rester possible.
        setGetReservations({ skip, take: RESERVATIONS_PAGE_SIZE });
        if (params && params.refetch) {
            refetchReservations();
        }
        return { myReservationsSkip: skip };
    });

    store.on('setMyReservations', (state, result) => {
        store.dispatch("setMyReservationsLoading", false);
        // Agent.send renvoie une instance d'Error sur erreur GraphQL/réseau.
        if (result instanceof Error || !result) {
            return { myReservations: null, myReservationsTotal: 0, myReservationsError: true };
        }
        return {
            myReservations: result.items,
            myReservationsTotal: result.totalItems,
            myReservationsError: false
        };
    });

    // Annulation d'une réservation puis rechargement de la liste (cf. store/Cart.js:65).
    // Le backend fait autorité : on se contente de relire la liste après coup.
    store.on('cancelReservation', async (state, orderLineId) => {
        // Une seule annulation à la fois : sans cette garde, un double-clic
        // envoie deux mutations. La seconde renvoie ALREADY_CANCELLED, et si
        // elle se résout en dernier l'usager lit "déjà annulée" alors que son
        // annulation a parfaitement fonctionné.
        if (state.cancellingReservation) {
            return;
        }
        store.dispatch("setCancellingReservation", orderLineId);
        try {
            store.dispatch("setCancelReservationResult", await agent.cancelReservation(orderLineId));
            await refetchReservations();
        } catch (err) {
            // Agent.send RENVOIE normalement une Error au lieu de rejeter. S'il
            // rejette quand même (throw interne, coupure réseau), sans ce catch
            // la promesse part en rejet non géré : aucune bannière, aucune liste
            // rechargée, l'usager a cliqué dans le vide.
            store.dispatch("setCancelReservationResult", err instanceof Error ? err : new Error());
        } finally {
            store.dispatch("setCancellingReservation", null);
        }
    });

    store.on('setCancellingReservation', (state, cancellingReservation) => ({ cancellingReservation }));

    store.on('setCancelReservationResult', (state, result) => {
        // Erreur GraphQL/réseau (dont ForbiddenError) => échec générique.
        if (result instanceof Error || !result) {
            return { cancelReservationResult: { success: false, errorCode: null } };
        }
        return { cancelReservationResult: result };
    });

    store.on('clearCancelReservationResult', () => ({ cancelReservationResult: null }));

    // Réservations d'UNE commande, depuis son seul code (lien e-mail, sans
    // session). Même pattern que ci-dessus : le backend fait autorité, on relit
    // la liste après chaque annulation (cf. store/Cart.js:65).
    const [getOrderReservations, setGetOrderReservations] = createSignal(false);
    const [orderReservations, { refetch: refetchOrderReservations }] = createResource(
        getOrderReservations,
        (params) => agent.getReservationsByOrderCode(params.code)
    );
    createEffect(() => {
        const result = orderReservations();
        if (result !== undefined) {
            store.dispatch("setOrderReservations", result);
        }
    });
    createEffect(() => orderReservations.loading && store.dispatch("setOrderReservationsLoading", orderReservations.loading));
    store.on('setOrderReservationsLoading', (state, orderReservations) => ({loading:{...state.loading, orderReservations}}));

    store.on('loadOrderReservations', (state, code) => {
        // Objet neuf à chaque fois : createResource compare par référence, donc
        // recharger le même code (après une annulation) doit rester possible.
        setGetOrderReservations({ code });
        // On repart d'une ardoise propre : sans ça, revenir sur la page après un
        // échec laisse la bannière rouge (ou la liste précédente) affichée
        // jusqu'à ce que la nouvelle requête réponde.
        return {
            orderReservationsCode: code,
            orderReservations: null,
            orderReservationsUnknownCode: false,
            orderReservationsError: false,
            loading: { ...state.loading, orderReservations: true }
        };
    });

    store.on('setOrderReservations', (state, result) => {
        store.dispatch("setOrderReservationsLoading", false);
        if (result instanceof Error) {
            // On ne retient QUE le refus d'autorisation du backend : code
            // inconnu, tronqué, ou commande non listable. Tester la seule
            // présence de graphQLErrors attraperait aussi une 500, une erreur
            // de validation ou un schéma désynchronisé — et on annoncerait au
            // client que SON lien est coupé alors que le serveur est en faute.
            // Il recopierait son URL indéfiniment sans jamais appeler personne.
            const unknownCode = (result.graphQLErrors || []).some(
                (e) => e && e.extensions && e.extensions.code === "FORBIDDEN"
            );
            return {
                orderReservations: null,
                orderReservationsUnknownCode: unknownCode,
                orderReservationsError: !unknownCode
            };
        }
        if (!result) {
            return {
                orderReservations: null,
                orderReservationsUnknownCode: false,
                orderReservationsError: true
            };
        }
        return {
            orderReservations: result,
            orderReservationsUnknownCode: false,
            orderReservationsError: false
        };
    });

    // Annulation depuis le lien e-mail, puis rechargement de la commande.
    // Même garde anti double-clic que cancelReservation.
    store.on('cancelReservationByCode', async (state, params) => {
        if (state.cancellingReservation) {
            return;
        }
        store.dispatch("setCancellingReservation", params.orderLineId);
        try {
            store.dispatch(
                "setCancelReservationResult",
                await agent.cancelReservationByCode(params.code, params.orderLineId)
            );
            await refetchOrderReservations();
        } catch (err) {
            // Agent.send RENVOIE normalement une Error au lieu de rejeter. S'il
            // rejette quand même, sans ce catch la promesse part en rejet non
            // géré : aucune bannière, aucune liste rechargée.
            store.dispatch("setCancelReservationResult", err instanceof Error ? err : new Error());
        } finally {
            store.dispatch("setCancellingReservation", null);
        }
    });

}
